import React, { useId, useState } from "react";
import { formatMetricCount } from "../utils/micrositeMetricFormat";
import { normalizePlatformAllocationKey } from "../utils/mediaPlanAllocations";
import { DataLayerGroupGrid } from "./RecommendedDataLayers";
import "./CampaignProposalPlanning.css";

/*
 * CAMPAIGN PROPOSAL PLANNING — the proposal story on the strategy microsite.
 *
 * Renders `campaign.sections.proposalPlanning` (backend
 * campaign_proposal_planning_v1): investment scenarios, the CTV forecast, the
 * audience targeting architecture, provider-reported data signals, inventory
 * and bidding context, KPI expectations, and assumptions. Everything shown is
 * served by the governed microsite payload — nothing campaign-specific lives
 * in this bundle — so INTERNAL REVIEW / CLIENT REVIEW / PUBLIC gating applies
 * exactly as it does to the rest of the body.
 *
 * PROPOSAL MODE. When a campaign carries this section the page tells ONE
 * story, in the owner's order: identity → objective + flight → investment
 * scenarios → CTV forecast → audience architecture → data signals →
 * inventory & delivery → KPI expectations → assumptions → full narrative.
 * Proposal mode is ADDITIVE. Template sections whose claims the proposal
 * contradicts (purchase / CPA / ROAS estimates, retargeting stages, search or
 * pixel activation, earned-media and release programs, generic cohort splits)
 * are withheld; presentation the proposal can feed — the planner charts and
 * the Impression Outlook — stays, re-sourced from the proposal. Byte-identical
 * in Cortex and Quantum.
 */

export const PROPOSAL_PLANNING_SCHEMA_VERSION = "campaign_proposal_planning_v1";

/*
 * Page sections withheld while a proposal is bound, each for the claim it
 * would contradict:
 *   whyThisStrategyWorks / proofStatStrip — conversion pressure, retargeting
 *     ROI and TikTok conversion lift on a CTV-only awareness plan;
 *   audienceCohorts — invented cohort shares; replaced by the architecture;
 *   earnedMedia / releaseMilestones / brandAwareness — earned, release and
 *     purchase-traffic programs outside the plan;
 *   antiWaste / scenarioTriggers — retargeting budgets, conversion tracking and
 *     multi-channel reallocation on a single-channel plan;
 *   benchmarkJustification / retargeting — "no provider forecast" and a
 *     directional CPM beside a partner forecast; a retargeting stage;
 *   launchCalendar (its activation milestones; the spend ramp is projected
 *     below) / successMetrics / actionItems — first-party list seeding,
 *     a Search launch, CPA ceilings, ROAS and purchase-transaction estimates;
 *   kpiMeasurement / didYouKnow — replaced by KPI Expectations; generic stats.
 */
export const PROPOSAL_MODE_WITHHELD_SECTIONS = Object.freeze([
  "whyThisStrategyWorks",
  "proofStatStrip",
  "audienceCohorts",
  "earnedMedia",
  "releaseMilestones",
  "antiWaste",
  "scenarioTriggers",
  "benchmarkJustification",
  "retargeting",
  "launchCalendar",
  "successMetrics",
  "actionItems",
  "brandAwareness",
  "kpiMeasurement",
  "didYouKnow",
]);

const WITHHELD = new Set(PROPOSAL_MODE_WITHHELD_SECTIONS);

export const isSectionWithheldInProposalMode = (planning, sectionKey) =>
  Boolean(planning) && WITHHELD.has(sectionKey);

/*
 * Page sections a proposal RE-SOURCES rather than withholds. Their template
 * data was directional (Campaign 53: 311,000 impressions from $16,000 ÷ a
 * $51.43 planning benchmark); hiding them removed the Budget Allocation and
 * Funnel Stage donuts, the Flight Spend chart, Platform Detail and the
 * Impression Outlook along with the bad number. In proposal mode they draw
 * from the proposal and the governed allocation instead:
 *   mediaPlanCharts — buildProposalMediaPlanChartProjection;
 *   impressionOutlook (outlook card, CPM card, funnel estimate rows) —
 *     buildProposalImpressionOutlook.
 * launchCalendar is split: its activation milestones stay withheld (list
 * seeding, retargeting pools, social / PR, per-store expansion), its governed
 * Weekly Spend Ramp chart is projected (buildProposalSpendRampCalendar).
 */
export const PROPOSAL_MODE_PROJECTED_SECTIONS = Object.freeze(["mediaPlanCharts", "impressionOutlook"]);

// ---------------------------------------------------------------------------
// Formatting — actual figures everywhere (utils/micrositeMetricFormat: the one
// microsite rule, no "K" / "M" abbreviations); CPMs to the cent.
// ---------------------------------------------------------------------------

const isFiniteNumber = (value) => typeof value === "number" && Number.isFinite(value);

export const formatProposalCount = (value) => (isFiniteNumber(value) ? formatMetricCount(value) : "");

export const formatProposalCurrency = (value) =>
  isFiniteNumber(value) ? `$${Math.round(value).toLocaleString("en-US")}` : "";

export const formatProposalCpm = (value) =>
  isFiniteNumber(value)
    ? `$${value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : "";

export const formatProposalCpmRange = (low, high) =>
  isFiniteNumber(low) && isFiniteNumber(high) ? `${formatProposalCpm(low)}–${formatProposalCpm(high)}` : "";

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const parseDateOnly = (value) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || ""));
  return match ? { year: Number(match[1]), month: Number(match[2]) - 1, day: Number(match[3]) } : null;
};

export const formatProposalFlightRange = (start, end) => {
  const a = parseDateOnly(start);
  const b = parseDateOnly(end);
  if (!a || !b) return "";
  if (a.year === b.year && a.month === b.month) {
    return `${MONTHS[a.month]} ${a.day}–${b.day}, ${a.year}`;
  }
  if (a.year === b.year) return `${MONTHS[a.month]} ${a.day} – ${MONTHS[b.month]} ${b.day}, ${a.year}`;
  return `${MONTHS[a.month]} ${a.day}, ${a.year} – ${MONTHS[b.month]} ${b.day}, ${b.year}`;
};

// ---------------------------------------------------------------------------
// Resolver — fail closed: a malformed or incomplete section renders nothing,
// never a half proposal.
// ---------------------------------------------------------------------------

const asArray = (value) => (Array.isArray(value) ? value : []);
const isNonEmptyString = (value) => typeof value === "string" && value.trim().length > 0;
const isNonEmptyArray = (value) => Array.isArray(value) && value.length > 0;
const allFinite = (source, keys) => keys.every((key) => isFiniteNumber(source?.[key]));

/*
 * Every part of the story renders unconditionally, and proposal mode withholds
 * the directional sections it replaces — so a section missing any part is
 * refused whole rather than traded for a half-rendered proposal.
 */
const hasCompleteProposalStory = (section, broad, qualified) => {
  const { audience, inventory, kpis } = section;
  return (
    isNonEmptyString(section.channel?.label) &&
    isNonEmptyString(section.geography) &&
    Boolean(parseDateOnly(section.flight?.start) && parseDateOnly(section.flight?.end)) &&
    [broad, qualified].every(
      (tier) =>
        isNonEmptyString(tier.label) &&
        isNonEmptyString(tier.evidenceLabel) &&
        allFinite(tier, ["workingCpm", "clientCpm"])
    ) &&
    allFinite(qualified, ["workingCpmLow", "workingCpmHigh", "clientCpmLow", "clientCpmHigh"]) &&
    isNonEmptyArray(audience?.layers) &&
    audience.layers.every((layer) => isNonEmptyString(layer?.order) && isNonEmptyString(layer?.title)) &&
    isNonEmptyArray(audience?.dataSignalGroups) &&
    audience.dataSignalGroups.every(
      (group) =>
        isNonEmptyString(group?.title) &&
        isNonEmptyArray(group?.segments) &&
        group.segments.every((segment) => isNonEmptyString(segment?.label))
    ) &&
    Array.isArray(audience?.catalogueSignals) &&
    isNonEmptyArray(inventory?.requirements) &&
    isNonEmptyArray(inventory?.structure) &&
    inventory.structure.every((entry) => isNonEmptyString(entry?.label) && allFinite(entry, ["shareLow", "shareHigh"])) &&
    isNonEmptyArray(inventory?.packages) &&
    inventory.packages.every(
      (entry) => isNonEmptyString(entry?.label) && allFinite(entry, ["bidLow", "bidHigh", "clientBidLow", "clientBidHigh"])
    ) &&
    allFinite(section.pricingContext, [
      "broadBid",
      "broadClientCpm",
      "dataBidGuidanceLow",
      "dataBidGuidanceHigh",
      "dataClientCpmLow",
      "dataClientCpmHigh",
    ]) &&
    isNonEmptyArray(kpis?.primary) &&
    isNonEmptyString(kpis?.successRead) &&
    isNonEmptyArray(section.caveats)
  );
};

export const resolveCampaignProposalPlanning = (campaign) => {
  const section = campaign?.sections?.proposalPlanning;
  if (!section || section.schemaVersion !== PROPOSAL_PLANNING_SCHEMA_VERSION) return null;

  const scenarios = asArray(section.scenarios).filter(
    (scenario) =>
      scenario &&
      isFiniteNumber(scenario.clientInvestment) &&
      isFiniteNumber(scenario.workingMedia) &&
      isFiniteNumber(scenario.broad?.impressions) &&
      isFiniteNumber(scenario.broad?.modeledReach) &&
      isFiniteNumber(scenario.broad?.averageFrequency) &&
      isFiniteNumber(scenario.qualified?.impressions)
  );
  const tiers = asArray(section.tiers);
  const broad = tiers.find((tier) => tier?.key === "broad");
  const qualified = tiers.find((tier) => tier?.key === "qualified");
  if (!scenarios.length || scenarios.length !== asArray(section.scenarios).length) return null;
  if (!broad || !qualified || !hasCompleteProposalStory(section, broad, qualified)) return null;

  return {
    ...section,
    scenarios,
    broad,
    qualified,
    flightLabel: formatProposalFlightRange(section.flight?.start, section.flight?.end),
  };
};

/**
 * The proposal as Eden reads it: the same client-facing figures, labels and
 * caveats the story renders, formatted the same way. Provider-reported sizes
 * keep their label and are never summed.
 */
export const buildProposalAssistantContext = (planning) => {
  if (!planning) return null;
  return {
    basis: "Client proposal: partner CTV forecast and planning estimates",
    channel: planning.channel?.label || "",
    geography: planning.geography || "",
    flight: planning.flightLabel || "",
    scenarios: planning.scenarios.map((scenario) => ({
      clientInvestment: formatProposalCurrency(scenario.clientInvestment),
      workingMedia: formatProposalCurrency(scenario.workingMedia),
      currentPlan: Boolean(scenario.isCurrentBudget),
      broadForecast: {
        impressions: formatProposalCount(scenario.broad.impressions),
        modeledReach: formatProposalCount(scenario.broad.modeledReach),
        averageFrequency: scenario.broad.averageFrequency.toFixed(2),
      },
      qualifiedPlanningImpressions: formatProposalCount(scenario.qualified.impressions),
    })),
    cpmTiers: [planning.broad, planning.qualified].map((tier) => ({
      label: tier.label,
      basis: tier.evidenceLabel,
      workingCpm: formatProposalCpm(tier.workingCpm),
      clientCpm: formatProposalCpm(tier.clientCpm),
      reachAndFrequency: tier.hasReachFrequency ? "modeled" : "not modeled",
    })),
    audienceLayers: asArray(planning.audience?.layers).map((layer) => ({
      order: layer.order,
      title: layer.title,
      purpose: layer.purpose || "",
    })),
    dataSignals: asArray(planning.audience?.dataSignalGroups).flatMap((group) =>
      asArray(group.segments).map((segment) => ({
        group: group.title,
        label: segment.label,
        providerReportedAudienceSize: formatProposalCount(segment.size),
      }))
    ),
    inventoryRequirements: asArray(planning.inventory?.requirements),
    kpis: asArray(planning.kpis?.primary),
    reporting: asArray(planning.kpis?.reporting),
    successRead: planning.kpis?.successRead || "",
    caveats: asArray(planning.caveats),
  };
};

// ---------------------------------------------------------------------------
// Projections — the planner charts and the Impression Outlook drawn from the
// proposal instead of directional benchmark math. Every figure names its
// basis; the broad forecast and the qualified planning estimate are
// alternative cases for the same working media and are never added together.
// ---------------------------------------------------------------------------

const currentProposalScenario = (planning) => {
  const current = asArray(planning?.scenarios).filter((scenario) => scenario.isCurrentBudget);
  return current.length === 1 ? current[0] : null;
};

const firstFiniteNumber = (values) => {
  for (const value of values) {
    const numeric =
      typeof value === "number" ? value : Number.parseFloat(String(value ?? "").replace(/[$%,\s]/g, ""));
    if (Number.isFinite(numeric)) return numeric;
  }
  return null;
};

/* Explicit share fields only: a served row's `value` can be dollars (16000), not a percent. */
const allocationShare = (row) => firstFiniteNumber([row?.percent, row?.allocationPercent, row?.share, row?.allocation]);
const allocationAmount = (row) => firstFiniteNumber([row?.grossAmount, row?.budget]);
const isFundedAllocationRow = (row) => (allocationShare(row) || 0) > 0 || (allocationAmount(row) || 0) > 0;

const allocationRowKey = (row) =>
  normalizePlatformAllocationKey(
    row?.key || row?.platformKey || row?.platform_key || row?.label || row?.platform || row?.name || "",
    { allowUnknown: true }
  );

const proposalChannelKey = (planning) =>
  normalizePlatformAllocationKey(planning?.channel?.key || planning?.channel?.label || "", { allowUnknown: true });

/*
 * A projection may present the proposal's channel as the whole plan only while
 * the governed allocation says so: every row funded by a share OR an amount is
 * that channel, and the stated shares total 100%. No funded rows is the same
 * claim — the backend binds a proposal only while the campaign's platforms are
 * exactly its channel.
 */
const governedAllocationMatchesProposal = (planning, allocationRows) => {
  const funded = asArray(allocationRows).filter(isFundedAllocationRow);
  if (!funded.length) return true;
  const channelKey = proposalChannelKey(planning);
  if (!channelKey || funded.some((row) => allocationRowKey(row) !== channelKey)) return false;
  const shares = funded.map(allocationShare).filter(isFiniteNumber);
  return !shares.length || Math.abs(shares.reduce((sum, share) => sum + share, 0) - 100) < 0.5;
};

/*
 * THE ONE GATE for every proposal projection — the charts, the Impression
 * Outlook, the funnel estimate rows and the spend ramp. A single current
 * scenario whose channel the governed allocation confirms; otherwise the page
 * shows none of them rather than a proposal that disagrees with the allocation.
 */
const governedProposalScenario = (planning, allocationRows) => {
  const current = currentProposalScenario(planning);
  return current && governedAllocationMatchesProposal(planning, allocationRows) ? current : null;
};

/* The campaign's governed objective as a funnel stage ("awareness" → "Awareness / Reach"). */
export const resolveProposalObjectiveStage = (objective) => {
  const text = String(objective || "").replace(/[_-]+/g, " ").trim();
  if (!text) return "";
  if (/\b(awareness|reach)\b/i.test(text)) return "Awareness / Reach";
  if (/\b(consideration|engagement)\b/i.test(text)) return "Consideration / Engagement";
  return text.replace(/\b[a-z]/g, (letter) => letter.toUpperCase());
};

const tierBasisLabel = (tier) => tier.cpmBasisLabel || tier.evidenceLabel;

/**
 * SherlocMediaPlanCharts `projection` for a bound proposal: the Budget
 * Allocation donut on client investment (working media stated beside it,
 * never mixed in), the Funnel Stage donut from the campaign objective, the
 * Flight Spend bar with the broad forecast, and Platform Detail with one row
 * per planning case. Null — render nothing — when the governed allocation and
 * the proposal disagree or the current scenario is ambiguous.
 */
export const buildProposalMediaPlanChartProjection = (planning, { allocationRows = [], objective = "" } = {}) => {
  const current = governedProposalScenario(planning, allocationRows);
  if (!current) return null;
  const { broad, qualified } = planning;
  const channel = planning.channel.label;
  const stage = resolveProposalObjectiveStage(objective);
  const primaryKpi = asArray(planning.kpis?.primary)[0] || "";
  const objectiveLabel = [stage, primaryKpi].filter(Boolean).join(" — ");
  const investment = formatProposalCurrency(current.clientInvestment);
  const working = formatProposalCurrency(current.workingMedia);
  const broadImpressions = formatProposalCount(current.broad.impressions);
  const qualifiedImpressions = formatProposalCount(current.qualified.impressions);

  return {
    totalBudget: current.clientInvestment,
    centerLabel: ["client", "investment"],
    budgetBasis: `Basis: client investment (campaign budget) · ${investment}`,
    budgetSecondaryBasis: `Working media, stated separately: ${working}`,
    funnelBasis: `Basis: client investment · Campaign objective: ${objectiveLabel || channel}`,
    spendBasis: `Spend basis: client investment · Impressions: ${broad.evidenceLabel}, ${broad.label}, on ${working} working media`,
    deliveryNote: `Alternative planning case, not additive: ${qualified.label} — ${qualifiedImpressions} estimated impressions on the same ${working} working media (${qualified.evidenceLabel}).`,
    platforms: [
      {
        key: planning.channel.key,
        name: channel,
        budget: current.clientInvestment,
        percent: 100,
        estimatedImpressions: current.broad.impressions,
        primaryObjective: objectiveLabel,
        funnelStage: stage,
        tooltipRows: [
          ["Client investment", investment],
          ["Allocation", "100%"],
          ["Working media", working],
          ["Broad forecast", `${broadImpressions} impressions`],
          ["Client CPM", formatProposalCpm(broad.clientCpm)],
          ...(objectiveLabel ? [["Objective", objectiveLabel]] : []),
        ],
      },
    ],
    funnelStages: stage
      ? [
          {
            name: stage,
            budget: current.clientInvestment,
            percent: 100,
            tooltipRows: [
              ["Client investment", investment],
              ["Share", "100%"],
              ...(primaryKpi ? [["Primary KPI", primaryKpi]] : []),
            ],
          },
        ]
      : [],
    detail: {
      rows: [
        {
          key: "broad",
          platform: channel,
          caseLabel: broad.label,
          caseNote: "Active forecast",
          investment,
          investmentNote: `${working} working media`,
          workingCpm: formatProposalCpm(broad.workingCpm),
          workingCpmNote: tierBasisLabel(broad),
          clientCpm: formatProposalCpm(broad.clientCpm),
          clientCpmNote: "Client-facing CPM",
          impressions: broadImpressions,
          impressionsNote: broad.evidenceLabel,
          objective: objectiveLabel,
        },
        {
          key: "qualified",
          platform: channel,
          caseLabel: qualified.label,
          caseNote: "Alternative planning case",
          investment,
          investmentNote: `Same ${working} working media · not additive`,
          workingCpm: `~${formatProposalCpm(qualified.workingCpm)}`,
          workingCpmNote: `${tierBasisLabel(qualified)} · ${formatProposalCpmRange(
            qualified.workingCpmLow,
            qualified.workingCpmHigh
          )}`,
          clientCpm: `~${formatProposalCpm(qualified.clientCpm)}`,
          clientCpmNote: formatProposalCpmRange(qualified.clientCpmLow, qualified.clientCpmHigh),
          impressions: qualifiedImpressions,
          impressionsNote: qualified.evidenceLabel,
          objective: objectiveLabel,
        },
      ],
      footnote: `Both rows describe the same ${investment} client investment (${working} working media): alternative planning cases, never added together. Reach and frequency are modeled for the broad forecast only.`,
    },
  };
};

/**
 * The Impression Outlook for a bound proposal: the current scenario's broad
 * forecast (impressions, modeled reach, average frequency), the qualified
 * planning estimate as an alternative case beside it — never a total — the
 * other investment scenario, and client-facing CPMs by planning case.
 */
export const buildProposalImpressionOutlook = (planning, { allocationRows = [] } = {}) => {
  const current = governedProposalScenario(planning, allocationRows);
  if (!current) return null;
  const { broad, qualified } = planning;
  const channel = planning.channel.label;
  const channelKey = planning.channel.key;
  const investment = formatProposalCurrency(current.clientInvestment);
  const working = formatProposalCurrency(current.workingMedia);
  const broadImpressions = formatProposalCount(current.broad.impressions);
  const reach = formatProposalCount(current.broad.modeledReach);
  const frequency = current.broad.averageFrequency.toFixed(2);
  const qualifiedImpressions = formatProposalCount(current.qualified.impressions);
  const qualifiedNote = `${qualified.evidenceLabel} · alternative case, not additive`;

  return {
    meta: broad.evidenceLabel,
    scenarioLabel: `Current plan · ${investment} client investment · ${working} working media`,
    headline: { label: "Broad forecast impressions", value: broadImpressions },
    bars: [
      {
        key: "broad",
        label: broad.label,
        rawValue: current.broad.impressions,
        ariaLabel: `${broad.label}: ${broadImpressions} forecast impressions on ${working} working media, ${reach} modeled reach, ${frequency} average frequency`,
        tooltipDatum: {
          key: channelKey,
          name: broad.label,
          tooltipRows: [
            ["Forecast impressions", broadImpressions],
            ["Modeled reach", reach],
            ["Avg. frequency", frequency],
            ["Working media", working],
            ["Basis", broad.evidenceLabel],
          ],
        },
      },
      {
        key: "qualified",
        label: qualified.label,
        rawValue: current.qualified.impressions,
        ariaLabel: `${qualified.label}: ${qualifiedImpressions} estimated impressions on the same ${working} working media, an alternative case, not additive`,
        tooltipDatum: {
          key: channelKey,
          name: qualified.label,
          tooltipRows: [
            ["Estimated impressions", qualifiedImpressions],
            ["Reach / frequency", "Not modeled"],
            ["Working media", `Same ${working}`],
            ["Basis", qualifiedNote],
          ],
        },
      },
    ],
    cases: [
      {
        key: "broad",
        channelKey,
        label: broad.label,
        value: broadImpressions,
        note: broad.evidenceLabel,
        figures: [
          ["Modeled reach", reach],
          ["Avg. frequency", frequency],
        ],
      },
      {
        key: "qualified",
        channelKey,
        label: qualified.label,
        value: qualifiedImpressions,
        note: qualifiedNote,
        figures: [["Reach / frequency", "Not modeled"]],
      },
    ],
    alternates: planning.scenarios
      .filter((scenario) => scenario !== current)
      .map((scenario) => ({
        key: scenario.key,
        label: `Alternate investment · ${formatProposalCurrency(scenario.clientInvestment)} client investment · ${formatProposalCurrency(
          scenario.workingMedia
        )} working media`,
        figures: [
          ["Broad forecast impressions", formatProposalCount(scenario.broad.impressions)],
          ["Modeled reach", formatProposalCount(scenario.broad.modeledReach)],
          ["Avg. frequency", scenario.broad.averageFrequency.toFixed(2)],
          ["Qualified planning estimate", formatProposalCount(scenario.qualified.impressions)],
        ],
        note: "Broad forecast and qualified planning estimate are alternative cases, not additive.",
      })),
    cpm: {
      title: "CPM by Planning Case",
      meta: "Client-facing CPM",
      cards: [
        {
          key: "broad",
          channelKey,
          label: broad.label,
          value: formatProposalCpm(broad.clientCpm),
          meta: `${formatProposalCpm(broad.workingCpm)} working · ${tierBasisLabel(broad)} · ${broad.evidenceLabel}`,
        },
        {
          key: "qualified",
          channelKey,
          label: qualified.label,
          value: `~${formatProposalCpm(qualified.clientCpm)}`,
          meta: `${formatProposalCpmRange(qualified.clientCpmLow, qualified.clientCpmHigh)} · ~${formatProposalCpm(
            qualified.workingCpm
          )} working · ${tierBasisLabel(qualified)} · ${qualified.evidenceLabel}`,
        },
      ],
    },
    funnelRows: [
      { key: "broad", channelKey, label: `${channel} · broad forecast`, value: broadImpressions },
      { key: "qualified", channelKey, label: `${channel} · qualified alternative`, value: qualifiedImpressions },
    ],
  };
};

/**
 * The Weekly Spend Ramp for a bound proposal: the backend's governed ramp — the
 * client investment on an even daily pacing baseline across the flight — with
 * its basis restated and the template activation calendar withheld (list
 * seeding, retargeting pools, social / PR amplification and per-store expansion
 * sit outside a CTV-only awareness plan). Null unless the governed allocation
 * confirms the proposal, every segment and legend entry is its one channel, the
 * weeks span exactly the proposal's flight, the backend's own reconciliation
 * (when published) is exact, and the weeks total the current client investment.
 */
export const buildProposalSpendRampCalendar = (planning, calendar, { allocationRows = [] } = {}) => {
  const current = governedProposalScenario(planning, allocationRows);
  const ramp = calendar?.spendRamp;
  const weeks = asArray(ramp?.weeks);
  const channelKey = proposalChannelKey(planning);
  if (!current || !weeks.length || !channelKey) return null;
  const isChannel = (entry) =>
    normalizePlatformAllocationKey(entry?.key || entry?.platformLabel || entry?.label || "", { allowUnknown: true }) ===
    channelKey;
  const segments = weeks.flatMap((week) => asArray(week?.segments));
  const totalCents = segments.reduce(
    (sum, segment) =>
      sum + (isFiniteNumber(segment?.valueCents) ? segment.valueCents : Math.round(Number(segment?.value) * 100)),
    0
  );
  const spansFlight =
    weeks[0]?.startDate === planning.flight?.start && weeks[weeks.length - 1]?.endDate === planning.flight?.end;
  const reconciled =
    !ramp.reconciliation || (ramp.reconciliation.exact === true && ramp.reconciliation.matchesCampaignBudget !== false);
  if (
    !segments.length ||
    !segments.every(isChannel) ||
    !asArray(ramp.platforms).every(isChannel) ||
    !spansFlight ||
    !reconciled ||
    totalCents !== Math.round(current.clientInvestment * 100)
  ) {
    return null;
  }
  return {
    ...calendar,
    title: "Weekly Spend Ramp",
    disclaimer: `Basis: client investment — ${formatProposalCurrency(
      current.clientInvestment
    )} on an even daily pacing baseline across ${planning.flightLabel}. Forecast impressions are modeled on ${formatProposalCurrency(
      current.workingMedia
    )} working media.`,
    milestones: [],
  };
};

// ---------------------------------------------------------------------------
// Building blocks
// ---------------------------------------------------------------------------

function ProposalSection({ sectionKey, kicker, title, meta, intro, children }) {
  const headingId = useId();
  return (
    <section
      className={`campaign-strategy-microsite-section-card campaign-strategy-microsite-proposal-section campaign-strategy-microsite-proposal-section--${sectionKey}`}
      aria-labelledby={headingId}
      data-proposal-section={sectionKey}
    >
      <div className="campaign-strategy-microsite-section-head">
        <div>
          <span className="campaign-strategy-microsite-section-kicker">{kicker}</span>
          <h3 id={headingId} className="campaign-strategy-microsite-section-title">
            {title}
          </h3>
        </div>
        {meta ? <span className="campaign-strategy-microsite-section-meta">{meta}</span> : null}
      </div>
      {intro ? <p className="campaign-strategy-microsite-section-intro">{intro}</p> : null}
      {children}
    </section>
  );
}

function ChipList({ items, variant = "", label }) {
  const entries = asArray(items).filter(Boolean);
  if (!entries.length) return null;
  return (
    <ul
      className={`campaign-strategy-microsite-proposal-chips${variant ? ` campaign-strategy-microsite-proposal-chips--${variant}` : ""}`}
      aria-label={label}
    >
      {entries.map((item) => (
        <li key={item} className="campaign-strategy-microsite-chip campaign-strategy-microsite-proposal-chip">
          {item}
        </li>
      ))}
    </ul>
  );
}

const scenarioWorkingLabel = (scenario) => `${formatProposalCurrency(scenario.workingMedia)} working media`;
const scenarioInvestmentLabel = (scenario) =>
  `${formatProposalCurrency(scenario.clientInvestment)} client investment`;

// ---------------------------------------------------------------------------
// 1. Investment scenarios
// ---------------------------------------------------------------------------

export function ProposalInvestmentScenarios({ planning }) {
  return (
    <ProposalSection
      sectionKey="investment"
      kicker="Investment Scenarios"
      title="Client Investment + Working Media"
      meta={`${planning.scenarios.length} scenarios`}
      intro="Two investment levels are modeled. Client investment is the amount billed; working media is the part of that investment applied directly to CTV media delivery, and every forecast below is modeled on working media."
    >
      <div className="campaign-strategy-microsite-proposal-scenario-grid">
        {planning.scenarios.map((scenario, index) => (
          <article
            key={scenario.key}
            className={`campaign-strategy-microsite-chart-card campaign-strategy-microsite-proposal-scenario-card${
              scenario.isCurrentBudget ? " is-current" : ""
            }`}
          >
            <div className="campaign-strategy-microsite-card-header">
              <span className="campaign-strategy-microsite-card-title">
                {`Scenario ${String.fromCharCode(65 + index)}`}
              </span>
              <span className="campaign-strategy-microsite-card-meta campaign-strategy-microsite-proposal-scenario-tag">
                {scenario.isCurrentBudget ? "Current plan budget" : "Alternate investment level"}
              </span>
            </div>
            <dl className="campaign-strategy-microsite-proposal-scenario-figures">
              <div className="campaign-strategy-microsite-proposal-scenario-figure is-investment">
                <dt>Client investment</dt>
                <dd>{formatProposalCurrency(scenario.clientInvestment)}</dd>
              </div>
              <div className="campaign-strategy-microsite-proposal-scenario-figure is-working">
                <dt>Working media</dt>
                <dd>{formatProposalCurrency(scenario.workingMedia)}</dd>
              </div>
            </dl>
          </article>
        ))}
      </div>
    </ProposalSection>
  );
}

// ---------------------------------------------------------------------------
// 2. CTV forecast — grouped horizontal bars (dataviz: 2 categorical series,
// validated palette #219cc0 / #a653df on the card surface; legend + direct
// value labels + table view; per-bar hover AND focus readout).
// ---------------------------------------------------------------------------

/*
 * The smallest axis maximum that splits into three clean intervals
 * (1.5 / 3 / 6 × 10^k → ticks at 0.5 / 1 / 2 × 10^k): 563,616 → 600,000 with
 * ticks at 0 / 200K / 400K / 600K.
 */
const niceAxisMax = (value) => {
  if (!(value > 0)) return 3;
  let magnitude = 10 ** Math.floor(Math.log10(value));
  for (;;) {
    for (const base of [1.5, 3, 6]) {
      if (base * magnitude >= value) return base * magnitude;
    }
    magnitude *= 10;
  }
};

const axisTicks = (max) => {
  const interval = max / 3;
  return [0, 1, 2, 3].map((index) => Math.round(interval * index));
};

export function ProposalForecastChart({ planning }) {
  const [activeBar, setActiveBar] = useState(null);
  const [showTable, setShowTable] = useState(false);
  const titleId = useId();
  const tableId = useId();
  const series = [
    { key: "broad", tier: planning.broad, pick: (scenario) => scenario.broad.impressions },
    { key: "qualified", tier: planning.qualified, pick: (scenario) => scenario.qualified.impressions },
  ];
  const peak = Math.max(...planning.scenarios.flatMap((scenario) => series.map((s) => s.pick(scenario))));
  const axisMax = niceAxisMax(peak * 1.02);
  const ticks = axisTicks(axisMax);

  return (
    <figure className="campaign-strategy-microsite-proposal-chart" aria-labelledby={titleId}>
      <figcaption id={titleId} className="campaign-strategy-microsite-proposal-chart-title">
        Forecast impressions by working media
      </figcaption>

      <div className="campaign-strategy-microsite-proposal-tradeoff" aria-hidden="true">
        <span className="campaign-strategy-microsite-proposal-tradeoff-end is-scale">More scale</span>
        <span className="campaign-strategy-microsite-proposal-tradeoff-rail" />
        <span className="campaign-strategy-microsite-proposal-tradeoff-end is-qualified">
          More audience qualification
        </span>
      </div>

      <ul className="campaign-strategy-microsite-proposal-legend" aria-label="Forecast series">
        {series.map(({ key, tier }) => (
          <li key={key} className={`campaign-strategy-microsite-proposal-legend-item is-${key}`}>
            <span className="campaign-strategy-microsite-proposal-swatch" aria-hidden="true" />
            <span className="campaign-strategy-microsite-proposal-legend-copy">
              <strong>{tier.label}</strong>
              <span>
                {key === "qualified"
                  ? `~${formatProposalCpm(tier.workingCpm)} working CPM · ~${formatProposalCpm(tier.clientCpm)} client CPM`
                  : `${formatProposalCpm(tier.workingCpm)} working CPM bid · ${formatProposalCpm(tier.clientCpm)} client CPM`}
              </span>
              <em>{tier.evidenceLabel}</em>
            </span>
          </li>
        ))}
      </ul>

      <div className="campaign-strategy-microsite-proposal-plot">
        {planning.scenarios.map((scenario) => (
          <div key={scenario.key} className="campaign-strategy-microsite-proposal-plot-group" role="group" aria-label={scenarioWorkingLabel(scenario)}>
            <div className="campaign-strategy-microsite-proposal-plot-label">
              <strong>{scenarioWorkingLabel(scenario)}</strong>
              <span>
                {scenarioInvestmentLabel(scenario)}
                {scenario.isCurrentBudget ? " · current plan" : ""}
              </span>
            </div>
            <div className="campaign-strategy-microsite-proposal-plot-bars">
              {series.map(({ key, tier, pick }) => {
                const value = pick(scenario);
                const barKey = `${scenario.key}:${key}`;
                const percent = Math.max(0, Math.min(100, (value / axisMax) * 100));
                const readout =
                  key === "broad"
                    ? `${formatProposalCount(scenario.broad.modeledReach)} modeled reach · ${scenario.broad.averageFrequency.toFixed(2)} avg. frequency`
                    : "Planning estimate · reach and frequency not modeled";
                return (
                  <div
                    key={key}
                    className={`campaign-strategy-microsite-proposal-bar-row is-${key}${activeBar === barKey ? " is-active" : ""}`}
                    style={{ "--proposal-bar-percent": `${percent}%` }}
                  >
                    <div className="campaign-strategy-microsite-proposal-bar-track">
                      <span
                        className="campaign-strategy-microsite-proposal-bar-fill"
                        tabIndex={0}
                        role="img"
                        aria-label={`${tier.label}, ${scenarioWorkingLabel(scenario)}: ${formatProposalCount(value)} ${
                          key === "broad" ? "forecast" : "estimated"
                        } impressions. ${readout}.`}
                        onMouseEnter={() => setActiveBar(barKey)}
                        onMouseLeave={() => setActiveBar((current) => (current === barKey ? null : current))}
                        onFocus={() => setActiveBar(barKey)}
                        onBlur={() => setActiveBar((current) => (current === barKey ? null : current))}
                      />
                      {activeBar === barKey ? (
                        <span className="campaign-strategy-microsite-proposal-tooltip" role="tooltip">
                          <strong>{formatProposalCount(value)}</strong>
                          <span>{`${key === "broad" ? "Forecast" : "Estimated"} impressions · ${tier.label}`}</span>
                          <span>{readout}</span>
                        </span>
                      ) : null}
                    </div>
                    <span className="campaign-strategy-microsite-proposal-bar-value" aria-hidden="true">
                      {formatProposalCount(value)}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
        <div className="campaign-strategy-microsite-proposal-axis" aria-hidden="true">
          <span />
          <div className="campaign-strategy-microsite-proposal-axis-ticks">
            {ticks.map((tick) => (
              <span key={tick} style={{ left: `${(tick / axisMax) * 100}%` }}>
                {formatProposalCount(tick)}
              </span>
            ))}
          </div>
        </div>
      </div>

      <button
        type="button"
        className="campaign-strategy-microsite-proposal-table-toggle"
        aria-expanded={showTable}
        aria-controls={tableId}
        onClick={() => setShowTable((value) => !value)}
      >
        {showTable ? "Hide forecast table" : "View forecast table"}
      </button>
      <div id={tableId} hidden={!showTable}>
        <table className="campaign-strategy-microsite-proposal-table">
          <caption>CTV forecast and planning estimates by scenario</caption>
          <thead>
            <tr>
              <th scope="col">Scenario</th>
              <th scope="col">Client investment</th>
              <th scope="col">Working media</th>
              <th scope="col">Broad forecast impressions</th>
              <th scope="col">Modeled reach</th>
              <th scope="col">Avg. frequency</th>
              <th scope="col">Qualified-data est. impressions</th>
            </tr>
          </thead>
          <tbody>
            {planning.scenarios.map((scenario, index) => (
              <tr key={scenario.key}>
                <th scope="row" data-label="Scenario">{`Scenario ${String.fromCharCode(65 + index)}`}</th>
                <td data-label="Client investment">{formatProposalCurrency(scenario.clientInvestment)}</td>
                <td data-label="Working media">{formatProposalCurrency(scenario.workingMedia)}</td>
                <td data-label="Broad forecast impressions">{formatProposalCount(scenario.broad.impressions)}</td>
                <td data-label="Modeled reach">{formatProposalCount(scenario.broad.modeledReach)}</td>
                <td data-label="Avg. frequency">{scenario.broad.averageFrequency.toFixed(2)}</td>
                <td data-label="Qualified-data est. impressions">{formatProposalCount(scenario.qualified.impressions)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </figure>
  );
}

export function ProposalCtvForecast({ planning }) {
  return (
    <ProposalSection
      sectionKey="forecast"
      kicker="Scale vs. Audience Qualification"
      title="CTV Forecast"
      meta="Partner forecast + planning estimate"
      intro="Forecast scale varies with audience qualification. Broader premium CTV inventory provides greater impression volume at the current benchmark, while purchaser- and faith-qualified data carries a higher planning CPM in exchange for tighter audience definition."
    >
      <div className="campaign-strategy-microsite-chart-card campaign-strategy-microsite-proposal-chart-card">
        <ProposalForecastChart planning={planning} />
      </div>

      <div className="campaign-strategy-microsite-proposal-forecast-grid">
        <article className="campaign-strategy-microsite-chart-card campaign-strategy-microsite-proposal-kpi-card is-broad">
          <div className="campaign-strategy-microsite-card-header">
            <span className="campaign-strategy-microsite-card-title">{planning.broad.label}</span>
            <span className="campaign-strategy-microsite-card-meta">{planning.broad.evidenceLabel}</span>
          </div>
          <p className="campaign-strategy-microsite-proposal-note">
            Reach and frequency belong to this broad / value forecast only.
          </p>
          {planning.scenarios.map((scenario) => (
            <div key={scenario.key} className="campaign-strategy-microsite-proposal-kpi-row">
              <span className="campaign-strategy-microsite-proposal-kpi-scenario">
                {scenarioWorkingLabel(scenario)}
              </span>
              <dl className="campaign-strategy-microsite-proposal-kpi-tiles">
                <div>
                  <dt>Forecast impressions</dt>
                  <dd>{formatProposalCount(scenario.broad.impressions)}</dd>
                </div>
                <div>
                  <dt>Modeled reach</dt>
                  <dd>{formatProposalCount(scenario.broad.modeledReach)}</dd>
                </div>
                <div>
                  <dt>Avg. frequency</dt>
                  <dd>{scenario.broad.averageFrequency.toFixed(2)}</dd>
                </div>
              </dl>
            </div>
          ))}
        </article>

        <article className="campaign-strategy-microsite-chart-card campaign-strategy-microsite-proposal-kpi-card is-qualified">
          <div className="campaign-strategy-microsite-card-header">
            <span className="campaign-strategy-microsite-card-title">{planning.qualified.label}</span>
            <span className="campaign-strategy-microsite-card-meta">{planning.qualified.evidenceLabel}</span>
          </div>
          <p className="campaign-strategy-microsite-proposal-note">
            {`Impressions at ~${formatProposalCpm(planning.qualified.workingCpm)} working CPM (${formatProposalCpmRange(
              planning.qualified.workingCpmLow,
              planning.qualified.workingCpmHigh
            )}; ${formatProposalCpmRange(planning.qualified.clientCpmLow, planning.qualified.clientCpmHigh)} client). No reach or frequency has been modeled for this scenario.`}
          </p>
          {planning.scenarios.map((scenario) => (
            <div key={scenario.key} className="campaign-strategy-microsite-proposal-kpi-row">
              <span className="campaign-strategy-microsite-proposal-kpi-scenario">
                {scenarioWorkingLabel(scenario)}
              </span>
              <dl className="campaign-strategy-microsite-proposal-kpi-tiles">
                <div>
                  <dt>Estimated impressions</dt>
                  <dd>{formatProposalCount(scenario.qualified.impressions)}</dd>
                </div>
                <div className="is-unavailable">
                  <dt>Reach / frequency</dt>
                  <dd>Not modeled</dd>
                </div>
              </dl>
            </div>
          ))}
        </article>
      </div>

      <div className="campaign-strategy-microsite-proposal-footnotes">
        <p>Planning estimates subject to final audience selection, inventory availability and December/Q4 market pricing.</p>
        <p>{`Forecast conditions: ${asArray(planning.forecastConditions).join(" · ")}.`}</p>
      </div>
    </ProposalSection>
  );
}

// ---------------------------------------------------------------------------
// 3. Audience targeting architecture
// ---------------------------------------------------------------------------

export function ProposalAudienceArchitecture({ planning }) {
  const layers = asArray(planning.audience?.layers);
  if (!layers.length) return null;
  return (
    <ProposalSection
      sectionKey="audience-architecture"
      kicker="Audience Strategy"
      title="Audience Targeting Architecture"
      meta={`${layers.length} layers`}
    >
      <ol className="campaign-strategy-microsite-proposal-layer-grid">
        {layers.map((layer) => (
          <li key={layer.key} className="campaign-strategy-microsite-chart-card campaign-strategy-microsite-proposal-layer">
            <span className="campaign-strategy-microsite-proposal-layer-order" aria-hidden="true">
              {layer.order}
            </span>
            <h4 className="campaign-strategy-microsite-proposal-layer-title">{layer.title}</h4>
            <p className="campaign-strategy-microsite-proposal-layer-purpose">{layer.purpose}</p>
            <ul className="campaign-strategy-microsite-proposal-layer-primary">
              {asArray(layer.primary).map((chip) => (
                <li key={chip.label}>
                  <span className="campaign-strategy-microsite-proposal-layer-chip-label">{chip.label}</span>
                  {chip.role ? (
                    <span className="campaign-strategy-microsite-proposal-role">{chip.role}</span>
                  ) : null}
                  {chip.detail ? (
                    <span className="campaign-strategy-microsite-proposal-layer-chip-detail">{chip.detail}</span>
                  ) : null}
                </li>
              ))}
            </ul>
            <ChipList items={layer.secondary} variant="secondary" label={`${layer.title} — also evaluated`} />
          </li>
        ))}
      </ol>
      {planning.audience?.householdSignalNote ? (
        <p className="campaign-strategy-microsite-proposal-note campaign-strategy-microsite-proposal-note--centered">
          {planning.audience.householdSignalNote}
        </p>
      ) : null}
    </ProposalSection>
  );
}

// ---------------------------------------------------------------------------
// 4. Audience data signals — provider-reported sizes, never summed. The group
// grid is the shared Recommended Data Layers renderer; this proposal shows
// its CTV bid guidance because the owner disclosed its economics.
// ---------------------------------------------------------------------------

const proposalBidGuidance = (segment) =>
  isFiniteNumber(segment.bidGuidance)
    ? {
        label: "CTV bid guidance",
        value: `${formatProposalCpm(segment.bidGuidance)} working · ${formatProposalCpm(segment.clientBidGuidance)} client`,
      }
    : null;

export function ProposalAudienceDataSignals({ planning }) {
  const groups = asArray(planning.audience?.dataSignalGroups);
  const catalogue = asArray(planning.audience?.catalogueSignals);
  if (!groups.length && !catalogue.length) return null;
  return (
    <ProposalSection
      sectionKey="data-signals"
      kicker="Provider / Data Signals"
      title="Recommended Data Layers"
      meta="Provider-reported sizes"
      intro="Provider-reported audience sizes use each provider's own definition — they are not people, households or unique reach, and they are never added together."
    >
      <DataLayerGroupGrid groups={groups} pricingFor={proposalBidGuidance} />
      {catalogue.length ? (
        <div className="campaign-strategy-microsite-chart-card campaign-strategy-microsite-proposal-catalogue">
          <div className="campaign-strategy-microsite-card-header">
            <span className="campaign-strategy-microsite-card-title">Also visible in the audience catalogue</span>
            <span className="campaign-strategy-microsite-card-meta">Provider-reported audience size</span>
          </div>
          <ul className="campaign-strategy-microsite-proposal-catalogue-list">
            {catalogue.map((signal) => (
              <li key={signal.label} className="campaign-strategy-microsite-proposal-catalogue-item">
                <span className="campaign-strategy-microsite-proposal-signal-label">{signal.label}</span>
                <span className="campaign-strategy-microsite-proposal-catalogue-size">
                  {isFiniteNumber(signal.size) ? formatProposalCount(signal.size) : "Size not reported"}
                </span>
                {signal.citedByStrategy ? (
                  <span className="campaign-strategy-microsite-proposal-role">Cited in strategy</span>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </ProposalSection>
  );
}

// ---------------------------------------------------------------------------
// 5. Inventory & delivery, with Q4 / holiday bidding context
// ---------------------------------------------------------------------------

export function ProposalInventoryDelivery({ planning }) {
  const inventory = planning.inventory || {};
  const pricing = planning.pricingContext || {};
  const requirements = [...asArray(inventory.requirements)];
  if (planning.flightLabel) requirements.push(`${planning.flightLabel.replace(/, \d{4}$/, "")} flight`);
  return (
    <ProposalSection
      sectionKey="inventory"
      kicker="Inventory + Delivery"
      title="Premium CTV Delivery"
      meta={planning.geography || planning.channel?.label}
    >
      <ChipList items={requirements} variant="requirements" label="Delivery requirements" />
      <div className="campaign-strategy-microsite-proposal-inventory-grid">
        <article className="campaign-strategy-microsite-chart-card campaign-strategy-microsite-proposal-structure">
          <div className="campaign-strategy-microsite-card-header">
            <span className="campaign-strategy-microsite-card-title">Recommended structure</span>
            <span className="campaign-strategy-microsite-card-meta">Share of working media</span>
          </div>
          <ul className="campaign-strategy-microsite-proposal-structure-list">
            {asArray(inventory.structure).map((entry) => (
              <li
                key={entry.label}
                style={{ "--proposal-share-low": `${entry.shareLow}%`, "--proposal-share-high": `${entry.shareHigh}%` }}
              >
                <div className="campaign-strategy-microsite-proposal-structure-head">
                  <span>{entry.label}</span>
                  <strong>{`${entry.shareLow}–${entry.shareHigh}%`}</strong>
                </div>
                <span className="campaign-strategy-microsite-proposal-range-track" aria-hidden="true">
                  <span className="campaign-strategy-microsite-proposal-range-fill" />
                </span>
                <p>{entry.detail}</p>
              </li>
            ))}
          </ul>
        </article>

        <article className="campaign-strategy-microsite-chart-card campaign-strategy-microsite-proposal-packages">
          <div className="campaign-strategy-microsite-card-header">
            <span className="campaign-strategy-microsite-card-title">Packages under consideration</span>
            <span className="campaign-strategy-microsite-card-meta">CTV bid guidance</span>
          </div>
          <ul className="campaign-strategy-microsite-proposal-package-list">
            {asArray(inventory.packages).map((entry) => (
              <li key={entry.label}>
                <div className="campaign-strategy-microsite-proposal-package-head">
                  <span>{entry.label}</span>
                  <strong>{formatProposalCpmRange(entry.bidLow, entry.bidHigh)}</strong>
                </div>
                <p>{`${entry.note} · ${formatProposalCpmRange(entry.clientBidLow, entry.clientBidHigh)} client`}</p>
              </li>
            ))}
          </ul>
          {inventory.publisherNote ? (
            <p className="campaign-strategy-microsite-proposal-note">{inventory.publisherNote}</p>
          ) : null}
        </article>
      </div>

      <article className="campaign-strategy-microsite-chart-card campaign-strategy-microsite-proposal-pricing">
        <div className="campaign-strategy-microsite-card-header">
          <span className="campaign-strategy-microsite-card-title">Q4 / holiday pricing + bidding context</span>
          <span className="campaign-strategy-microsite-card-meta">Working · client CPM</span>
        </div>
        <dl className="campaign-strategy-microsite-proposal-pricing-figures">
          <div>
            <dt>{planning.broad.label}</dt>
            <dd>{`${formatProposalCpm(pricing.broadBid)} working bid · ${formatProposalCpm(pricing.broadClientCpm)} client`}</dd>
          </div>
          <div>
            <dt>Faith / purchaser data segments</dt>
            <dd>
              {`${formatProposalCpmRange(pricing.dataBidGuidanceLow, pricing.dataBidGuidanceHigh)} working · ${formatProposalCpmRange(
                pricing.dataClientCpmLow,
                pricing.dataClientCpmHigh
              )} client`}
            </dd>
          </div>
        </dl>
        <ul className="campaign-strategy-microsite-bullet-list campaign-strategy-microsite-proposal-bullets">
          {asArray(pricing.notes).map((note) => (
            <li key={note}>{note}</li>
          ))}
        </ul>
      </article>
    </ProposalSection>
  );
}

// ---------------------------------------------------------------------------
// 6. KPI expectations
// ---------------------------------------------------------------------------

export function ProposalKpiExpectations({ planning }) {
  const kpis = planning.kpis || {};
  return (
    <ProposalSection sectionKey="kpis" kicker="Measurement" title="KPI Expectations" meta="Awareness">
      <div className="campaign-strategy-microsite-proposal-kpi-expectations">
        <article className="campaign-strategy-microsite-chart-card">
          <div className="campaign-strategy-microsite-card-header">
            <span className="campaign-strategy-microsite-card-title">Primary KPIs</span>
          </div>
          <ChipList items={kpis.primary} variant="primary" label="Primary KPIs" />
          {kpis.successRead ? <p className="campaign-strategy-microsite-proposal-note">{kpis.successRead}</p> : null}
        </article>
        <article className="campaign-strategy-microsite-chart-card">
          <div className="campaign-strategy-microsite-card-header">
            <span className="campaign-strategy-microsite-card-title">Delivery reporting</span>
          </div>
          <ChipList items={kpis.reporting} label="Delivery reporting" />
        </article>
      </div>
      {kpis.notImplied ? (
        <p className="campaign-strategy-microsite-proposal-callout">{kpis.notImplied}</p>
      ) : null}
    </ProposalSection>
  );
}

// ---------------------------------------------------------------------------
// 7. Assumptions + watchouts
// ---------------------------------------------------------------------------

export function ProposalAssumptions({ planning }) {
  const caveats = asArray(planning.caveats);
  const confirmations = asArray(planning.openConfirmations);
  if (!caveats.length && !confirmations.length) return null;
  return (
    <ProposalSection
      sectionKey="assumptions"
      kicker="Planning Caveats"
      title="Assumptions / Watchouts"
      meta="Planning caveats"
    >
      <div className="campaign-strategy-microsite-proposal-assumption-grid">
        <article className="campaign-strategy-microsite-chart-card">
          <div className="campaign-strategy-microsite-card-header">
            <span className="campaign-strategy-microsite-card-title">Planning caveats</span>
          </div>
          <ul className="campaign-strategy-microsite-bullet-list campaign-strategy-microsite-proposal-bullets">
            {caveats.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </article>
        <article className="campaign-strategy-microsite-chart-card">
          <div className="campaign-strategy-microsite-card-header">
            <span className="campaign-strategy-microsite-card-title">Open confirmations</span>
            <span className="campaign-strategy-microsite-card-meta">Before trafficking</span>
          </div>
          <ul className="campaign-strategy-microsite-bullet-list campaign-strategy-microsite-proposal-bullets">
            {confirmations.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </article>
      </div>
    </ProposalSection>
  );
}

// ---------------------------------------------------------------------------
// The story, in the owner's order, and the narrative note
// ---------------------------------------------------------------------------

export function ProposalNarrativeNote({ planning }) {
  if (!planning?.narrativeNote) return null;
  return <p className="campaign-strategy-microsite-proposal-narrative-note">{planning.narrativeNote}</p>;
}

export default function CampaignProposalPlanningStory({ planning }) {
  if (!planning) return null;
  return (
    <div className="campaign-strategy-microsite-proposal-story" data-testid="campaign-proposal-planning">
      <ProposalInvestmentScenarios planning={planning} />
      <ProposalCtvForecast planning={planning} />
      <ProposalAudienceArchitecture planning={planning} />
      <ProposalAudienceDataSignals planning={planning} />
      <ProposalInventoryDelivery planning={planning} />
      <ProposalKpiExpectations planning={planning} />
      <ProposalAssumptions planning={planning} />
    </div>
  );
}
